<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content modal-content-xlarge">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ $t('admin.support_prompts.test.title') }}</h1>
                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body test-panel">
                        <div class="columns is-gapless">
                            <!-- Settings Panel -->
                            <div class="column is-one-third settings-column">
                                <g-form :form="form" action="/api/admin/support-prompt-template/test"
                                        @loading-state="(state) => isFormLoading = state"
                                        method="post" @success="testSuccess">
                                    <div class="settings-panel">
                                        <!-- Test Data Section -->
                                        <div class="settings-section">
                                            <h4 class="settings-section-title">
                                                <i class="icon">science</i>
                                                {{ $t('admin.support_prompts.test.settings') }}
                                            </h4>

                                            <g-select
                                                name="subject"
                                                :label="$t('admin.support_prompts.subject')"
                                                :options="subjectOptions"
                                            />

                                            <g-select
                                                name="order_id"
                                                :label="$t('admin.support_prompts.test.order')"
                                                :options="orderOptions"
                                                :disabled="!!isLoadingOrders"
                                            />
                                            <p class="help mb-3">{{ $t('admin.support_prompts.test.order_hint') }}</p>

                                            <g-input
                                                name="custom_order_id"
                                                type="number"
                                                :label="$t('admin.support_prompts.test.custom_order_id')"
                                                :placeholder="$t('admin.support_prompts.test.custom_order_id_placeholder')"
                                                :disabled="!!form.order_id"
                                            />
                                            <p class="help mb-3">{{ $t('admin.support_prompts.test.custom_order_id_hint') }}</p>

                                            <div v-if="!form.order_id && !form.custom_order_id" class="mock-data-options">
                                                <g-input
                                                    name="company_id"
                                                    type="number"
                                                    :label="$t('admin.support_prompts.test.company_id')"
                                                    :placeholder="$t('admin.support_prompts.test.company_id_placeholder')"
                                                />
                                                <p class="help mb-3">{{ $t('admin.support_prompts.test.company_id_hint') }}</p>

                                                <g-select
                                                    name="test_language"
                                                    :label="$t('admin.support_prompts.test.language')"
                                                    :options="languageOptions"
                                                    :searchable="false"
                                                />
                                            </div>

                                            <g-input
                                                name="test_customer_message"
                                                type="textarea"
                                                :label="$t('admin.support_prompts.test.customer_message')"
                                                :placeholder="$t('admin.support_prompts.test.customer_message_placeholder')"
                                                rows="4"
                                            />
                                        </div>

                                        <!-- AI Execution Section -->
                                        <div class="settings-section">
                                            <h4 class="settings-section-title">
                                                <i class="icon">smart_toy</i>
                                                {{ $t('admin.support_prompts.test.ai_settings') }}
                                            </h4>

                                            <g-switch
                                                name="execute"
                                                :label="$t('admin.support_prompts.test.execute_ai')"
                                            />
                                            <p class="help mb-3">{{ $t('admin.support_prompts.test.execute_ai_hint') }}</p>

                                            <div v-if="form.execute" class="ai-options">
                                                <div class="columns is-mobile">
                                                    <div class="column">
                                                        <g-select
                                                            name="ai_service"
                                                            :label="$t('admin.support_prompts.ai_service')"
                                                            :options="aiServiceOptions"
                                                            @input="onServiceChange"
                                                        />
                                                    </div>
                                                    <div class="column">
                                                        <g-select
                                                            name="ai_model"
                                                            :label="$t('admin.support_prompts.ai_model')"
                                                            :options="aiModelOptions"
                                                            :disabled="!form.ai_service"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="columns is-mobile">
                                                    <div class="column">
                                                        <g-switch
                                                            name="enable_thinking"
                                                            :label="$t('admin.support_prompts.enable_thinking')"
                                                        />
                                                    </div>
                                                    <div class="column">
                                                        <g-input
                                                            name="temperature"
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            max="2"
                                                            :label="$t('admin.support_prompts.temperature')"
                                                            :disabled="!!form.enable_thinking"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <g-button class="is-fullwidth is-primary" type="submit" :loading="isFormLoading" icon="play_arrow">
                                            {{ $t('admin.support_prompts.test.run') }}
                                        </g-button>
                                    </div>
                                </g-form>
                            </div>

                            <!-- Results Panel -->
                            <div class="column is-two-thirds results-column">
                                <div class="results-panel">
                                    <div v-if="result && result.using_real_order" class="notification is-success is-light mb-3">
                                        <i class="icon">check_circle</i>
                                        {{ $t('admin.support_prompts.test.using_real_order') }}
                                    </div>

                                    <div class="tabs is-boxed">
                                        <ul>
                                            <li :class="{'is-active': activeTab === 'response'}">
                                                <a @click="activeTab = 'response'">
                                                    <i class="icon is-small">chat</i>
                                                    <span>{{ $t('admin.support_prompts.test.response') }}</span>
                                                </a>
                                            </li>
                                            <li v-if="result && result.debug && result.debug.thinking" :class="{'is-active': activeTab === 'thinking'}">
                                                <a @click="activeTab = 'thinking'">
                                                    <i class="icon is-small">psychology</i>
                                                    <span>{{ $t('admin.support_prompts.test.thinking') }}</span>
                                                </a>
                                            </li>
                                            <li v-if="result && result.debug" :class="{'is-active': activeTab === 'conversation'}">
                                                <a @click="activeTab = 'conversation'">
                                                    <i class="icon is-small">forum</i>
                                                    <span>{{ $t('admin.support_prompts.test.conversation') }}</span>
                                                </a>
                                            </li>
                                            <li :class="{'is-active': activeTab === 'system'}">
                                                <a @click="activeTab = 'system'">
                                                    <i class="icon is-small">terminal</i>
                                                    <span>{{ $t('admin.support_prompts.test.system_prompt') }}</span>
                                                </a>
                                            </li>
                                            <li :class="{'is-active': activeTab === 'task'}">
                                                <a @click="activeTab = 'task'">
                                                    <i class="icon is-small">assignment</i>
                                                    <span>{{ $t('admin.support_prompts.test.task_prompt') }}</span>
                                                </a>
                                            </li>
                                            <li :class="{'is-active': activeTab === 'parameters'}">
                                                <a @click="activeTab = 'parameters'">
                                                    <i class="icon is-small">data_object</i>
                                                    <span>{{ $t('admin.support_prompts.test.parameters') }}</span>
                                                </a>
                                            </li>
                                            <li :class="{'is-active': activeTab === 'timing'}">
                                                <a @click="activeTab = 'timing'">
                                                    <i class="icon is-small">timer</i>
                                                    <span>{{ $t('admin.support_prompts.test.timing') }}</span>
                                                    <span v-if="result && result.timing" class="tag is-small is-info ml-2">
                                                        {{ result.timing.total }}ms
                                                    </span>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>

                                <div class="tab-content">
                                    <div v-show="activeTab === 'response'" class="content-panel">
                                        <div v-if="result && result.generated_response">
                                            <h4 class="title is-6">{{ $t('admin.support_prompts.test.ai_response') }}</h4>
                                            <div class="content generated-response">
                                                <template v-for="(segment, index) in formattedResponseSegments">
                                                    <strong v-if="segment.bold" :key="`response-segment-${index}`">{{ segment.text }}</strong>
                                                    <span v-else :key="`response-segment-${index}`">{{ segment.text }}</span>
                                                </template>
                                            </div>

                                            <div v-if="result.debug && result.debug.usage" class="mt-4 pt-3" style="border-top: 1px solid #eee;">
                                                <span class="tag is-info is-light mr-2">
                                                    {{ $t('admin.support_prompts.test.service') }}: {{ result.debug.usage.service }}
                                                </span>
                                                <span class="tag is-light mr-2">
                                                    {{ $t('admin.support_prompts.test.model') }}: {{ result.debug.usage.model }}
                                                </span>
                                                <span class="tag is-light mr-2">
                                                    {{ $t('admin.support_prompts.temperature') }}: {{ result.debug.usage.temperature }}
                                                </span>
                                                <span class="tag is-light mr-2">
                                                    {{ $t('admin.support_prompts.test.input_tokens') }}: {{ result.debug.usage.input_tokens }}
                                                </span>
                                                <span class="tag is-light">
                                                    {{ $t('admin.support_prompts.test.output_tokens') }}: {{ result.debug.usage.output_tokens }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-else-if="result && !form.execute" class="notification is-info is-light">
                                            <i class="icon">info</i>
                                            {{ $t('admin.support_prompts.test.no_execute_hint') }}
                                        </div>
                                        <div v-else-if="!result" class="empty-state">
                                            <i class="icon empty-state-icon">chat</i>
                                            <p>{{ $t('admin.support_prompts.test.no_result') }}</p>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'thinking'" class="content-panel">
                                        <div v-if="result && result.debug && result.debug.thinking">
                                            <h4 class="title is-6">
                                                <i class="icon is-small">psychology</i>
                                                {{ $t('admin.support_prompts.test.thinking_title') }}
                                            </h4>
                                            <p class="help mb-3">{{ $t('admin.support_prompts.test.thinking_hint') }}</p>
                                            <pre class="code-block thinking-block">{{ result.debug.thinking }}</pre>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'conversation'" class="content-panel">
                                        <div v-if="result && result.debug">
                                            <h4 class="title is-6">
                                                <i class="icon is-small">forum</i>
                                                {{ $t('admin.support_prompts.test.conversation_title') }}
                                            </h4>
                                            <p class="help mb-3">{{ $t('admin.support_prompts.test.conversation_hint') }}</p>

                                            <div class="conversation-messages">
                                                <div v-for="(msg, index) in result.debug.messages_sent" :key="index"
                                                     class="conversation-message"
                                                     :class="'conversation-' + msg.role">
                                                    <div class="conversation-role">
                                                        <span class="tag" :class="msg.role === 'user' ? 'is-info' : 'is-success'">
                                                            {{ msg.role }}
                                                        </span>
                                                    </div>
                                                    <pre class="conversation-content">{{ msg.content }}</pre>
                                                </div>

                                                <div v-if="result.debug.raw_response" class="conversation-message conversation-assistant">
                                                    <div class="conversation-role">
                                                        <span class="tag is-warning">assistant (raw)</span>
                                                    </div>
                                                    <pre class="conversation-content">{{ result.debug.raw_response }}</pre>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'system'" class="content-panel">
                                        <pre v-if="result" class="code-block">{{ result.prompt.system_prompt }}</pre>
                                        <div v-else class="empty-state">
                                            <i class="icon empty-state-icon">terminal</i>
                                            <p>{{ $t('admin.support_prompts.test.no_result') }}</p>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'task'" class="content-panel">
                                        <pre v-if="result" class="code-block">{{ result.prompt.task_prompt }}</pre>
                                        <div v-else class="empty-state">
                                            <i class="icon empty-state-icon">assignment</i>
                                            <p>{{ $t('admin.support_prompts.test.no_result') }}</p>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'parameters'" class="content-panel">
                                        <pre v-if="result" class="code-block">{{ JSON.stringify(result.prompt.parameters, null, 2) }}</pre>
                                        <div v-else class="empty-state">
                                            <i class="icon empty-state-icon">data_object</i>
                                            <p>{{ $t('admin.support_prompts.test.no_result') }}</p>
                                        </div>
                                    </div>

                                    <div v-show="activeTab === 'timing'" class="content-panel">
                                        <div v-if="result && result.timing">
                                            <h4 class="title is-6">{{ $t('admin.support_prompts.test.timing_breakdown') }}</h4>
                                            <table class="table is-fullwidth">
                                                <thead>
                                                    <tr>
                                                        <th>{{ $t('admin.support_prompts.test.timing_operation') }}</th>
                                                        <th class="has-text-right">{{ $t('admin.support_prompts.test.timing_duration') }}</th>
                                                        <th class="has-text-right">%</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <i class="icon is-small">storage</i>
                                                            {{ $t('admin.support_prompts.test.timing_order_context') }}
                                                        </td>
                                                        <td class="has-text-right">{{ result.timing.order_context }} ms</td>
                                                        <td class="has-text-right">{{ calculatePercentage(result.timing.order_context) }}%</td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <i class="icon is-small">description</i>
                                                            {{ $t('admin.support_prompts.test.timing_prompt_generation') }}
                                                        </td>
                                                        <td class="has-text-right">{{ result.timing.prompt_generation }} ms</td>
                                                        <td class="has-text-right">{{ calculatePercentage(result.timing.prompt_generation) }}%</td>
                                                    </tr>
                                                    <tr v-if="result.timing.ai_api_call !== undefined">
                                                        <td>
                                                            <i class="icon is-small">smart_toy</i>
                                                            {{ $t('admin.support_prompts.test.timing_ai_api') }}
                                                        </td>
                                                        <td class="has-text-right">{{ result.timing.ai_api_call }} ms</td>
                                                        <td class="has-text-right">{{ calculatePercentage(result.timing.ai_api_call) }}%</td>
                                                    </tr>
                                                    <tr class="has-background-light">
                                                        <td><strong>{{ $t('admin.support_prompts.test.timing_total') }}</strong></td>
                                                        <td class="has-text-right"><strong>{{ result.timing.total }} ms</strong></td>
                                                        <td class="has-text-right"><strong>100%</strong></td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            <div class="timing-bar mt-4">
                                                <div class="timing-segment timing-context"
                                                     :style="{width: calculatePercentage(result.timing.order_context) + '%'}"
                                                     :title="$t('admin.support_prompts.test.timing_order_context')">
                                                </div>
                                                <div class="timing-segment timing-prompt"
                                                     :style="{width: calculatePercentage(result.timing.prompt_generation) + '%'}"
                                                     :title="$t('admin.support_prompts.test.timing_prompt_generation')">
                                                </div>
                                                <div v-if="result.timing.ai_api_call !== undefined"
                                                     class="timing-segment timing-ai"
                                                     :style="{width: calculatePercentage(result.timing.ai_api_call) + '%'}"
                                                     :title="$t('admin.support_prompts.test.timing_ai_api')">
                                                </div>
                                            </div>
                                            <div class="timing-legend mt-2">
                                                <span class="timing-legend-item">
                                                    <span class="timing-dot timing-context"></span>
                                                    {{ $t('admin.support_prompts.test.timing_order_context') }}
                                                </span>
                                                <span class="timing-legend-item">
                                                    <span class="timing-dot timing-prompt"></span>
                                                    {{ $t('admin.support_prompts.test.timing_prompt_generation') }}
                                                </span>
                                                <span v-if="result.timing.ai_api_call !== undefined" class="timing-legend-item">
                                                    <span class="timing-dot timing-ai"></span>
                                                    {{ $t('admin.support_prompts.test.timing_ai_api') }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-else class="empty-state">
                                            <i class="icon empty-state-icon">timer</i>
                                            <p>{{ $t('admin.support_prompts.test.no_result') }}</p>
                                        </div>
                                    </div>
                                </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="popup-footer">
                        <a @click.prevent="destroy" href="#" role="button">{{ $t('submit_dialog.cancel') }}</a>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    data() {
        return {
            isLoadingOrders: true,
            isFormLoading: true,
            activeTab: 'response',
            result: null,
            recentOrders: [],
            aiServices: {},
            aiModels: {},
            form: new Form({
                subject: 'delivery_inquiry',
                company_id: null,
                order_id: null,
                custom_order_id: null,
                test_language: 'nl',
                test_customer_message: '',
                execute: false,
                ai_service: null,
                ai_model: null,
                enable_thinking: true,
                temperature: null,
            }),
            subjectOptions: [
                {value: 'delivery_inquiry', name: 'Delivery Inquiry'},
            ],
            languageOptions: [
                {value: 'nl', name: 'Nederlands'},
                {value: 'en', name: 'English'},
                {value: 'de', name: 'Deutsch'},
            ],
        }
    },

    computed: {
        orderOptions() {
            const options = [
                {value: null, name: this.$t('admin.support_prompts.test.order_mock')},
            ]
            this.recentOrders.forEach(order => {
                options.push({value: order.id, name: order.label})
            })
            return options
        },

        aiServiceOptions() {
            const options = [{value: null, name: this.$t('admin.support_prompts.ai_service_default')}]
            Object.entries(this.aiServices).forEach(([key, label]) => {
                options.push({value: key, name: label})
            })
            return options
        },

        aiModelOptions() {
            const options = [{value: null, name: this.$t('admin.support_prompts.ai_model_default')}]
            if (this.form.ai_service && this.aiModels[this.form.ai_service]) {
                Object.entries(this.aiModels[this.form.ai_service]).forEach(([key, label]) => {
                    options.push({value: key, name: label})
                })
            }
            return options
        },

        formattedResponseSegments() {
            const response = this.result && this.result.generated_response

            if (!response) {
                return []
            }

            const text = typeof response === 'string' ? response : String(response)
            const segments = []
            const boldPattern = /\*\*([\s\S]+?)\*\*/g
            let match = null
            let lastIndex = 0

            while ((match = boldPattern.exec(text)) !== null) {
                if (match.index > lastIndex) {
                    segments.push({
                        text: text.slice(lastIndex, match.index),
                        bold: false,
                    })
                }

                segments.push({
                    text: match[1],
                    bold: true,
                })

                lastIndex = boldPattern.lastIndex
            }

            if (lastIndex < text.length) {
                segments.push({
                    text: text.slice(lastIndex),
                    bold: false,
                })
            }

            return segments
        },
    },

    created() {
        this.loadDefaults()
        this.loadRecentOrders()
    },

    methods: {
        loadDefaults() {
            axios.get('/api/admin/support-prompt-template/defaults')
                .then(response => {
                    this.subjectOptions = response.data.subjects.map(s => ({
                        value: s,
                        name: s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    }))
                    this.aiServices = response.data.ai_services || {}
                    this.aiModels = response.data.ai_models || {}
                })
                .catch(error => {
                    console.error('Failed to load defaults:', error)
                })
        },

        onServiceChange() {
            this.form.ai_model = null
        },

        loadRecentOrders() {
            axios.get('/api/admin/support-prompt-template/recent-orders')
                .then(response => {
                    this.recentOrders = response.data.orders
                })
                .catch(error => {
                    console.error('Failed to load recent orders:', error)
                })
                .finally(() => {
                    this.isLoadingOrders = false
                    this.isFormLoading = false
                })
        },

        testSuccess(response) {
            this.result = response

            if (response.generated_response) {
                this.activeTab = 'response'
            }
        },

        calculatePercentage(value) {
            if (!this.result || !this.result.timing || !this.result.timing.total) {
                return 0
            }
            return Math.round((value / this.result.timing.total) * 100)
        },
    },
}
</script>

<style scoped lang="scss">
.modal {
    .modal-content-xlarge {
        max-width: 1400px !important;
        width: 95%;
    }
}

.test-panel {
    padding: 0 !important;
}

.settings-column {
    background: #f8f9fa;
    border-right: 1px solid #e8e8e8;
}

.settings-panel {
    padding: 1.25rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.settings-section {
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e8e8e8;

    &:last-of-type {
        border-bottom: none;
        margin-bottom: 1rem;
    }
}

.settings-section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: #363636;
    margin-bottom: 1rem;

    .icon {
        color: #7a7a7a;
        font-size: 1.1rem;
    }
}

.mock-data-options,
.ai-options {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
}

.results-column {
    background: #fff;
}

.results-panel {
    padding: 1.25rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tabs.is-boxed {
    margin-bottom: 0;

    ul {
        border-bottom-color: #e8e8e8;
    }

    a {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.5em 0.85em;
        font-size: 0.85rem;

        .icon {
            font-size: 1rem;
        }
    }

    li.is-active a {
        background-color: #fff;
        border-color: #e8e8e8;
        border-bottom-color: transparent;
    }
}

.tab-content {
    flex: 1;
    padding: 1rem 0;
    overflow-y: auto;
}

.content-panel {
    min-height: 350px;
}

.generated-response {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #7a7a7a;
    text-align: center;

    .empty-state-icon {
        font-size: 3rem;
        color: #dbdbdb;
        margin-bottom: 1rem;
    }

    p {
        font-size: 0.95rem;
    }
}

.code-block {
    background: #f5f5f5;
    padding: 1rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    white-space: pre-wrap;
    word-wrap: break-word;
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e8e8e8;
}

.thinking-block {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-left: 4px solid #f59e0b;
}

.conversation-messages {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.conversation-message {
    border-radius: 8px;
    padding: 0.75rem 1rem;
    background: #f9f9f9;
    border: 1px solid #e8e8e8;
}

.conversation-user {
    background: #eff6ff;
    border-color: #bfdbfe;
    border-left: 4px solid #3b82f6;
}

.conversation-assistant {
    background: #fffbeb;
    border-color: #fde68a;
    border-left: 4px solid #f59e0b;
}

.conversation-role {
    margin-bottom: 0.5rem;
}

.conversation-content {
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: 0.8rem;
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    white-space: pre-wrap;
    word-wrap: break-word;
    max-height: 250px;
    overflow-y: auto;
}

.timing-bar {
    display: flex;
    height: 28px;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
    border: 1px solid #e8e8e8;
}

.timing-segment {
    height: 100%;
    min-width: 3px;
    transition: width 0.3s ease;
}

.timing-context {
    background: #3b82f6;
}

.timing-prompt {
    background: #10b981;
}

.timing-ai {
    background: #f59e0b;
}

.timing-legend {
    display: flex;
    gap: 1.25rem;
    flex-wrap: wrap;
    font-size: 0.8rem;
    color: #4a4a4a;
}

.timing-legend-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.timing-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
}

// Usage stats styling
.box {
    .tag {
        font-size: 0.75rem;
    }
}

.notification {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: flex-start;

    .icon {
        font-size: 1.25rem;
    }
}
</style>
